import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Header
    nav: {
      whyDoora: 'Why Doora',
      product: 'Product',
      research: 'Research',
      benchmark: 'Benchmark',
      results: 'Results',
      roi: 'ROI',
      getDemo: 'Get a Demo',
    },
    // Hero
    hero: {
      eyebrow: 'The AI Knowledge Layer',
      headline: 'The foundation that makes',
      headlineHighlight: 'AI investment',
      headlineEnd: 'pay off',
      subheadline: 'We sit between your enterprise systems and every AI application copilots, agents, search, and analytics delivering trusted, fully traceable answers.',
      ctaPrimary: 'See It In Action',
      ctaSecondary: 'View Industry Results',
      chips: ['SOC 2 Compliant', 'Deploy in Weeks', 'Zero Migration'],
      metrics: [
        { value: '73%', label: 'Faster Time-to-Answer' },
        { value: '4.2x', label: 'ROI in Year One' },
        { value: '89%', label: 'Employee Trust in AI' },
        { value: '60%', label: 'Less Duplicate Work' },
        { value: '$2.4M', label: 'Avg. Annual Savings' },
        { value: '3 Weeks', label: 'To Full Deployment' },
      ],
    },
    // Layer Architecture
    layers: {
      eyebrow: 'The AI Knowledge Layer',
      headline: 'A 5-Layer Architecture for ',
      headlineHighlight: 'Swarm Intelligence',
      subheadline: 'Doora processes structured, semi-structured, and unstructured data into a self-connecting Knowledge Graph.',
      layer5: { name: 'L5: Application', description: 'Enterprise copilot, knowledge explorer, dashboards, API' },
      layer4: { name: 'L4: Intelligence', description: 'Semantic retrieval, graph traversal, tracing' },
      layer3: { name: 'L3: Knowledge Storage', description: 'Knowledge Graph, Vector DB, Blob Objects' },
      layer2: { name: 'L2: Knowledge Processing', description: 'AI entity extraction, clustering, embedding' },
      layer1: { name: 'L1: Data Ingestion', description: 'Multi-source connectors normalizing all data types' },
      flowText: 'Swarm Intelligence Network • Fully Traceable AI Stack',
      layerLabel: 'Layer',
    },
    // Processing Engine
    processing: {
      eyebrow: 'Knowledge Processing Engine',
      headline: 'Unifying all your',
      headlineHighlight: 'data structures',
      subheadline: 'Behind the scenes, completely different AI pipelines process your data based on its shape so you can query an ERP database and a fragmented PDF in the exact same search.',
      sourcesLabel: 'Sources',
      structured: {
        title: 'Structured Data',
        description: 'Format with rigid schemas (Rows & Columns). Extremely fast to query.',
        sources: 'ERP, DBs, CRM, ITSM',
        process: 'NL2SQL → Query Safety → Analytical OLAP',
      },
      semiStructured: {
        title: 'Semi-Structured',
        description: 'Data with internal structure (tags/markers) but lacks rigid database schemas.',
        sources: 'Events, JSON Logs, Jira, Wiki',
        process: 'Parsing → Extract → Attribute Flattening',
      },
      unstructured: {
        title: 'Unstructured Data',
        description: 'Free text, web content, and rich documents that are traditionally hard to query.',
        sources: 'Cloud Docs, Chat Threads, Papers, Web',
        process: 'Chunking → Embedding → Semantic Vector Search',
      }
    },
    // Comparison
    comparison: {
      eyebrow: 'The AI Gap',
      headline: 'of enterprise AI projects',
      headlineHighlight: 'fail to deliver value',
      subheadline: "The problem isn't your AI models. It's the fragmented knowledge beneath them.",
      pain: 'Pain',
      withDoora: 'With Doora',
      rows: [
        { dimension: 'Time to Find Information', pain: '45+ minutes searching across systems', solution: 'Instant answers with full source attribution', impact: '73% faster' },
        { dimension: 'Trust in AI Responses', pain: 'No idea where answers come from', solution: 'Every answer traceable to source documents', impact: '89% adoption' },
        { dimension: 'Knowledge Sharing', pain: 'Siloed teams, duplicated work', solution: 'One unified company brain for everyone', impact: '60% less rework' },
        { dimension: 'AI Infrastructure Cost', pain: 'Rebuild everything for each new AI app', solution: 'One foundation powers unlimited AI use cases', impact: '16x lower cost' },
        { dimension: 'Data Freshness', pain: 'Stale data, manual refresh cycles', solution: 'Real-time sync, always up to date', impact: 'Always accurate' },
        { dimension: 'Compliance & Audit', pain: "Can't prove AI decision sources", solution: 'Complete audit trail for every response', impact: '100% auditable' },
      ],
      quote: "We stopped asking 'Can our AI do this?' and started asking 'What else can we build?'",
      quoter: '  VP of Digital Transformation',
    },
    // Features
    features: {
      eyebrow: 'Why Leading Enterprises Choose Doora',
      headline: 'Your AI is only as smart as',
      headlineHighlight: 'your knowledge layer',
      subheadline: 'Stop building AI on fragmented foundations. Give every team the same trusted intelligence.',
      items: [
        { title: 'Connect in Days, Not Months', subtitle: 'Instant Integration', description: 'Works with your existing systems no migration, no disruption. Your teams keep working while AI gets smarter.', stats: [{ value: '20+', label: 'Connectors' }, { value: '3 weeks', label: 'Deployment' }] },
        { title: 'One Brain for the Whole Company', subtitle: 'Unified Intelligence', description: 'Marketing knows what Sales knows. Engineering finds what Legal already solved. Everyone benefits from collective knowledge.', stats: [{ value: '60%', label: 'Less Rework' }, { value: '4.2x', label: 'Faster Onboarding' }] },
        { title: 'Always Fresh, Always Right', subtitle: 'Real-Time Sync', description: "New document? Searchable in seconds. Policy change? Everyone has the latest. No stale data, ever.", stats: [{ value: '~25s', label: 'Update Speed' }, { value: '100%', label: 'Accuracy' }] },
        { title: 'Trust Every Answer', subtitle: 'Source Attribution', description: 'Every sentence links to its source. Hover any response to see exactly where the information came from document, chunk, and confidence. Perfect for audits, compliance, and building trust.', stats: [{ value: '45.0', label: 'Attribution Signals' }, { value: '100%', label: 'Auditable' }] },
        { title: 'Export Knowledge for AI Training', subtitle: 'AI Integration Hub', description: 'Feed curated knowledge into external AI apps for training or context. Choose generative datasets for LLM/embedding workflows, or structured ML exports for classification, regression, and clustering.', stats: [{ value: '4', label: 'Export Modes' }, { value: 'API', label: 'Access' }] },
        { title: 'Built for the Enterprise', subtitle: 'Security & Control', description: 'Your data stays yours. Role-based access, audit logs, on-premise option. IT and Legal will love it.', stats: [{ value: 'SOC 2', label: 'Compliant' }, { value: 'RBAC', label: 'Controls' }] },
      ],
    },
    // Use Cases
    useCases: {
      eyebrow: 'Proven Results',
      headline: 'Real results across',
      headlineHighlight: 'every industry',
      subheadline: "These aren't projections. These are measured results from production deployments.",
      before: 'Before',
      after: 'After',
      annualCost: 'Annual Cost',
      viewDetails: 'View detailed metrics',
      showLess: 'Show less',
      industries: [
        { industry: 'Financial Services', company: 'Global Investment Bank', challenge: 'Compliance teams spent 6+ hours daily searching regulations across 50+ systems', before: { cost: '$4.2M/year' }, after: { cost: '$890K/year' }, impact: { primary: '79%', label: 'Cost Reduction' }, quote: '"Our compliance team now handles 3x the workload with higher confidence."', quoter: 'Chief Compliance Officer' },
        { industry: 'Telecommunications', company: 'National Telecom Provider', challenge: "Support agents couldn't quickly resolve technical network issues across 10+ disjointed KB systems", before: { cost: '$5.4M/year' }, after: { cost: '$1.2M/year' }, impact: { primary: '78%', label: 'Cost Reduction' }, quote: '"Agents now resolve technical issues on the first call without escalating to Tier 2."', quoter: 'VP of Customer Success' },
        { industry: 'Manufacturing', company: 'Automotive Supplier', challenge: 'Engineers recreated solutions that existed elsewhere, causing delays', before: { cost: '$6.1M/year' }, after: { cost: '$1.4M/year' }, impact: { primary: '77%', label: 'Less Rework' }, quote: '"We stopped reinventing the wheel. Literally we design wheels."', quoter: 'Director of Engineering' },
        { industry: 'Legal', company: 'Top 50 Law Firm', challenge: 'Associates spent 60% of time on research partners had already done', before: { cost: '$8.2M/year' }, after: { cost: '$2.1M/year' }, impact: { primary: '74%', label: 'Efficiency Gain' }, quote: '"Junior associates now perform like mid-levels. That\'s transformative."', quoter: 'Managing Partner' },
      ],
      bottomStats: [
        { value: '50+', label: 'Enterprise Deployments' },
        { value: '$18M+', label: 'Annual Savings Generated' },
        { value: '99.9%', label: 'Uptime SLA' },
      ],
    },
    // ROI
    roi: {
      eyebrow: 'Measured ROI from Production Deployments',
      headline: 'The ROI your CFO will',
      headlineHighlight: 'actually believe',
      subheadline: 'Not projections. Proof from enterprises like yours.',
      outcomes: [
        { stat: '73%', title: 'Faster Decisions', description: 'Instant answers vs. hours of searching' },
        { stat: '60%', title: 'Less Rework', description: 'Surface what others already solved' },
        { stat: '89%', title: 'AI Adoption', description: 'When people trust the answers' },
        { stat: '100%', title: 'Audit Ready', description: 'Every answer traceable to source' },
        { stat: '4.2x', title: 'Faster Onboarding', description: 'New hires productive from day one' },
        { stat: '16x', title: 'Lower AI Costs', description: 'One foundation, unlimited apps' },
      ],
      timelineTitle: 'Investment that',
      timelineHighlight: 'compounds over time',
      timeline: [
        { month: 'Month 1', title: 'Connect & Go Live', description: 'Connect core systems, see immediate search improvement' },
        { month: 'Month 3', title: 'Cross-Team Value', description: 'Knowledge sharing across departments, measurable time savings' },
        { month: 'Month 6+', title: 'AI Acceleration', description: 'Launch new AI apps in days, not months. Compounding returns.' },
      ],
    },
    // CTA
    cta: {
      badge: 'See Results in 3 Weeks',
      headline: 'Ready to turn company data into',
      headlineHighlight: 'competitive advantage?',
      subheadline: "Transformed your enterprise AI capabilities. We'll show you what's possible with your actual data.",
      emailPlaceholder: 'Work email',
      companyPlaceholder: 'Company name',
      submit: 'Schedule a Demo',
      trustIndicators: ['30-min personalized demo', 'Response within 24 hours'],
      successTitle: "You're on the list!",
      successMessage: "We'll reach out within 24 hours to schedule your personalized demo. Get ready to see what your data can really do.",
      errorMessage: 'Something went wrong. Please try again.',
      quote: '"The ROI conversation lasted 5 minutes. The results spoke for themselves."',
      quoter: '  CTO, Enterprise Software Company',
    },
    // Footer
    footer: {
      description: 'The AI Knowledge Layer that transforms fragmented enterprise knowledge into connected intelligence.',
      product: 'Product',
      company: 'Company',
      resources: 'Resources',
      legal: 'Legal',
      copyright: '© 2026 Doora. All rights reserved.',
      tagline: '',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      security: 'Security',
      about: 'About',
      blog: 'Blog',
      careers: 'Careers',
      contact: 'Contact',
    },
    // Indonesian Focus Section
    indonesianFocus: {
      eyebrow: 'Deep Integration Local Context',
      headline: 'Unmatched Precision in',
      headlineHighlight: 'Bahasa',
      subheadline: 'Generic global RAG models stumble on local idioms and mixed language (Bahasa slang).',
      card1: {
        title: 'Context-Aware Localization',
        description: 'Unlike off-the-shelf LLMs that loosely translate context, Doora understands specific enterprise jargons embedded inside Indonesian daily corporate communication, ensuring zero hallucinations in sensitive data mapping.',
      },
      card2: {
        title: 'Hybrid Embedding Models',
        description: 'Doora leverages fine-tuned embedding pipelines specifically trained on Indonesian linguistic structures, drastically outperforming generic retrieval engines in fetching the right documents.',
      },
      research: {
        badge: 'Backed by IEEE Research',
        published: 'Published',
        paperTitle: '"IndoEduBERT: Tailored Multi-Lingual and Multi-Grained Sentence Embeddings for the Indonesian Education Domain"',
        reviewLink: 'Review Publication',
      },
    },
    // Knowledge Graph diagram
    knowledgeGraph: {
      nodes: {
        analyticsPanel: 'Analytics Panel',
        verifiedAnswer: 'Verified Answer',
        autonomousAgent: 'Autonomous Agent',
        factChecker: 'Fact Checker',
        dooraKnowledgeGraph: 'Doora Knowledge Graph',
        vectorIndex: 'Vector Index',
        entityResolution: 'Entity Resolution',
        githubPR: 'GitHub PR #402',
        confluenceDocs: 'Confluence Docs',
        slackThread: 'Slack Thread',
      },
      header: { title: 'Traceability Engine', subtitle: 'Full Source Attribution' },
      matchBadge: '99% Match',
    },
    // Neural Network diagram
    neuralNetwork: {
      inputs: { database: 'Database', apis: 'APIs', crm: 'CRM', documents: 'Documents', cloud: 'Cloud', erp: 'ERP' },
      outputs: { copilot: 'Copilot', agent: 'Agent', search: 'Search', analytics: 'Analytics', apps: 'Apps', api: 'API' },
      dataSourcesLabel: 'DATA SOURCES',
      aiOutputsLabel: 'AI OUTPUTS',
    },
    // Complex Data Section
    complexData: {
      eyebrow: 'Built for Real-World Data',
      headline: 'Thriving in the',
      headlineHighlight: 'Chaos',
      headlineEnd: 'of Unstructured Data',
      subheadline: "Enterprise data isn't pristine; it's scattered in scanned files and legacy archives. Doora abandons conventional RAG pipelines, deploying specialized neural extraction methods specifically engineered for disarray-vastly outperforming standard retrieval platforms.",
      features: [
        { title: 'Extracting the Unextractable', description: 'Doora deploys Vision-Language Models (VLMs) and LayoutLMv3-based OCR to parse messy physical scans, complex tables, and unstructured PDFs.' },
        { title: 'Graph-Native Relational Extraction', description: 'Instead of naive chunking that destroys context, we map entities across legacy files. Expanding on SOTA GraphRAG research, we dynamically link isolated enterprise data into a cohesive graph.' },
        { title: 'Contextual Multi-Hop Reasoning', description: 'Standard vector search fails on poorly managed documents. We employ hierarchical parsing to preserve intent, vastly outperforming generic RAG in unstructured environments.' },
      ],
    },
    // Benchmark Section
    benchmark: {
      eyebrow: 'Independent Evaluation, Transparent Results',
      headline: 'Benchmarked against',
      headlineHighlight: 'the alternatives',
      subheadline: 'Doora vs Others — same model, same data, different retrieval',
      dooraLabel: 'Doora',
      vsLabel: 'vs',
      bestAlternative: 'Best Alternative',
      metricLabels: {
        retrievalRelevance: { metric: 'Retrieval Relevance', insight: 'Better answer quality across benchmarked scenarios' },
        attributionCoverage: { metric: 'Attribution Coverage', insight: 'Strong answer traceability and verifiability' },
        costPerQuery: { metric: 'Cost per Query', insight: 'Transparent cost tradeoff for higher accuracy' },
        incrementalUpdate: { metric: 'Incremental Update', insight: 'Better operational freshness' },
        graphConstructionCost: { metric: 'Graph Construction Cost', insight: '16.4x cheaper graph construction' },
      },
      useCaseSectionTitle: 'Performance by Use Case',
      tableHeaders: { useCase: 'Use Case', relevance: 'Relevance', coverage: 'Coverage', takeaway: 'Takeaway' },
      useCases: [
        { key: 'factualLookup', useCase: 'Factual Lookup', dooraScore: '47%', dooraAttr: '42.6', takeaway: 'More trustworthy for operational use' },
        { key: 'multiSourceSynthesis', useCase: 'Multi-Source Synthesis', dooraScore: '64%', dooraAttr: '62.4', takeaway: 'Strong synthesis with provenance' },
        { key: 'thematicDiscovery', useCase: 'Thematic Discovery', dooraScore: '68%', dooraAttr: '66.2', takeaway: 'Better transparency for org questions' },
        { key: 'relationshipDiscovery', useCase: 'Relationship Discovery', dooraScore: '80%', dooraAttr: '74', takeaway: 'This is where Doora shines' },
      ],
      detailedToggle: 'Detailed Performance Report (vs Alternatives)',
      detailedHeaders: { useCase: 'Use Case', fuseragDoora: 'FuseRAG (Doora)', graphrag: 'GraphRAG', hipporag: 'HippoRAG', adaptiverag: 'AdaptiveRAG', selfrag: 'SelfRAG', convrag: 'Conv. RAG' },
      detailedReport: [
        { useCase: 'Factual Lookup', fuserag: '47.2%', graphrag: '42.5%', hipporag: '38.1%', adaptiverag: '39.4%', selfrag: '35.2%', convrag: '31.5%' },
        { useCase: 'Multi-Source Synthesis', fuserag: '64.3%', graphrag: '62.4%', hipporag: '51.8%', adaptiverag: '55.1%', selfrag: '48.9%', convrag: '41.2%' },
        { useCase: 'Thematic Discovery', fuserag: '68.5%', graphrag: '66.2%', hipporag: '47.3%', adaptiverag: '46.0%', selfrag: '42.1%', convrag: '38.7%' },
        { useCase: 'Relationship Discovery', fuserag: '80.1%', graphrag: '74.5%', hipporag: '65.2%', adaptiverag: '61.8%', selfrag: '58.3%', convrag: '45.4%' },
      ],
      research: {
        title: 'Research-Backed Architecture',
        quote: '"Our novel FuseRAG approach natively combines sparse multi-source signals and dense topological graph patterns, effectively neutralizing context dilution and outperforming traditional retrieval by bridging cross-document entity gaps."',
        readThePaper: 'Read the Paper',
        paperTitle: 'FuseRAG: Parallel Multi-Source Knowledge Fusion for Hybrid Graph-Augmented Retrieval-Augmented Generation',
      },
      modal: {
        sampleTest: 'Sample Test',
        subtitle: 'Real-World Scenario Evaluation',
        userQuery: 'User Query',
        expectedAnswer: 'Expected Standard Answer',
        dooraOutput: 'Doora Output',
        highRelevanceClarity: 'High Relevance & Clarity',
        averageAlternative: 'Average Alternative',
        lacksNuance: 'Lacks Nuance / Context',
      },
      useCaseSamples: {
        factualLookup: {
          question: 'What was the Q3 revenue for the APAC region?',
          expected: 'The Q3 revenue for the APAC region was $4.2M.',
          doora: 'According to the Q3 Regional Financial Report (page 12), the APAC region generated $4.2M in revenue.',
          others: 'The revenue for APAC was $4.2M. (Often lacks structured context or exact page citations)',
        },
        multiSourceSynthesis: {
          question: 'How do the new compliance rules affect onboarding for remote contractors?',
          expected: 'Remote contractors require an extra security module and background check taking 2 additional days.',
          doora: 'Synthesizing the Contractor Policy and the 2024 Compliance Update: Remote contractors must complete the expanded security module and undergo a level-2 background check, adding approximately 2 days to their standard onboarding timeline.',
          others: 'Contractors need to undergo security training. (Frequently misses the synthesis of multiple policy updates and exact impacts)',
        },
        thematicDiscovery: {
          question: 'What are the core recurring themes in recent negative customer feedback?',
          expected: 'Frequent mentions of mobile app crashes, slow dashboard loading, and confusing invoice layouts.',
          doora: 'Based on 300+ recent support tickets, the primary negative themes are: 1. Dashboard latency during peak hours. 2. Confusion around prorated invoice items. 3. Unexpected crashes on the iOS mobile application.',
          others: 'Customers are complaining about the dashboard and some billing issues. (Fails to capture specific themes and nuance)',
        },
        relationshipDiscovery: {
          question: 'Who is the key stakeholder blocking the SSO Migration project, and what other initiatives are they tied to?',
          expected: 'Jane Doe is the required approver for the SSO Migration and is also tied to the CRM Overhaul.',
          doora: "Jane Doe (VP of IT) is the pending required approver preventing the SSO Migration state from changing to 'Active'. Graph analysis reveals she is also the project sponsor for the upcoming 'CRM Overhaul' initiative.",
          others: 'Jane Doe is the contact for the SSO Migration. (Struggles to traverse relationship hops to find block status or other aligned initiatives)',
        },
      },
    },
    // Product Page
    productPage: {
      badge: 'Doora Core Platform',
      headline: 'The AI Foundation for',
      headlineHighlight: 'Enterprises',
      subheadline: 'A fully integrated Retrieval-Augmented Generation ecosystem built for accuracy, compliance, and flawless operations in Southeast Asia.',
      features: [
        { title: 'Fine-tuned Indonesian NLP', desc: 'Custom embedding models for Bahasa ID slang & formal business vocabulary.' },
        { title: 'On-Premise Ready', desc: 'Full control. Deploy entirely within your own firewalls for zero data leaks.' },
        { title: 'Automated Knowledge Graphs', desc: 'Convert structured and unstructured documents into relational knowledge.' },
        { title: 'Enterprise Role-Based Access', desc: 'Granular ACLs down to the document snippet level.' },
      ],
      capabilitiesHeadline: 'Platform',
      capabilitiesHighlight: 'Capabilities',
      capabilitiesSubheadline: 'Explore the tools and features designed to make enterprise data accessible and secure without ever compromising your privacy.',
      capabilities: [
        { title: 'Intelligent Semantic Search', description: "Experience conversational AI that actually understands context. Query your entire organizational brain-from codebase to meeting notes-and receive synthesized, heavily-contextualized answers instantly." },
        { title: 'Transparent & Traceable Attribution', description: "Trust your AI's outputs with 100% verifiability. Every generated response includes deep, clickable citations back to the exact source documents, maintaining full audit trails and compliance." },
        { title: 'Interactive Knowledge Graph', description: 'Visualize the hidden relationships across your enterprise data. Our engine automatically extracts semantic connections between documents, repositories, people, and topics, forming a cohesive worldview of your organization.' },
        { title: 'Universal Knowledge Hub', description: 'Consolidate your scattered intelligence into a single pane of glass. Monitor ingestion status, manage data silos, and orchestrate the active sources that feed your customized AI models.' },
        { title: 'Plug-and-Play Integrations', description: 'Connect securely to over 35+ enterprise platforms out-of-the-box. From GitHub and GitLab to Google Scholar and Slack, sync your data securely without writing a single line of ETL code.' },
      ],
      ctaHeadline: 'Ready to see Doora in action?',
      ctaSubheadline: 'Book a personalized demo and see how Doora transforms your enterprise knowledge into a competitive advantage.',
      ctaButton: 'Request a Demo',
    },
    // Research Page
    researchPage: {
      headline: 'Why Enterprise AI',
      headlineHighlight: 'Fails',
      subheadline: 'The transition from pilot programs to production operations is where most enterprise AI initiatives collapse.',
      labels: { coreArgument: 'Core Argument', keyInsight: 'Key Insight', primaryRisk: 'Primary Risk', dooraDelivers: 'How Doora Delivers on This' },
      reports: [
        {
          id: 1, source: 'Gartner', date: 'January 2024',
          title: '30% of Generative AI Projects Will Be Abandoned After Proof of Concept by End of 2025',
          coreArgument: 'As organizations move from pilot to production, poor data quality, inadequate risk controls, and unclear business value lead to widespread project abandonment.',
          keyInsight: 'Pilots succeed in contained environments but fail to scale due to unstructured data nightmares and cost overruns.',
          primaryRisk: 'Sunk costs on LLM experimentation that yield zero operational ROI.',
          dooraResponse: [
            'Pre-retrieval data structuring via automated Knowledge Graphs guarantees high data fidelity.',
            'Transparent cost controls predict and cap LLM usage before a single token is generated.',
          ],
        },
        {
          id: 2, source: 'McKinsey', date: 'Late 2023 / Early 2024',
          title: 'The state of AI: The imperative for enterprise scaling',
          coreArgument: 'Achieving impact from AI requires fundamentally redesigning the operating model, not just adopting the technology itself.',
          keyInsight: 'Companies capturing the most value from AI focus heavily on domain-specific training and operational integration.',
          primaryRisk: 'Using generic models for specialized enterprise workflows leads to inaccurate handling of proprietary vocabulary.',
          dooraResponse: [
            'Domain-adapted embedding models are fine-tuned specifically for rich Indonesian enterprise language, including corporate jargon and local dialects.',
            "Deep operational integration tools allow AI workflows to map directly into your team's existing business processes.",
          ],
        },
        {
          id: 3, source: 'MIT Sloan', date: 'March 2024',
          title: 'Why so many generative AI projects fail',
          coreArgument: "Failures stem from an 'infrastructural debt' where companies attempt to stack AI on top of fragmented, unmanaged data silos.",
          keyInsight: 'AI models are only as good as the underlying data architecture. Unverified RAG outputs quickly erode user trust.',
          primaryRisk: 'Hallucinations causing critical business errors or regulatory breaches.',
          dooraResponse: [
            'Strict separation of grounded document retrieval from text generation completely eliminates unverified LLM hallucinations.',
            'Every factual claim is cross-verified against the original source documents, providing users with exact, clickable citations.',
          ],
        },
        {
          id: 4, source: 'IBM Institute for Business Value', date: 'CEO Study 2024',
          title: 'The AI Enterprise Dilemma: Governance and Ethics',
          coreArgument: 'CEOs recognize the potential of GenAI, but data security, privacy, and regulatory compliance remain the top barriers to integration.',
          keyInsight: "Enterprises need on-premise or highly secure environments to ensure proprietary knowledge doesn't leak into public models.",
          primaryRisk: 'Exposing sensitive intellectual property or customer data to third-party LLMs.',
          dooraResponse: [
            'Fully contained on-premise and virtual private cloud (VPC) deployments ensure confidential IP never leaves your firewall.',
            'Granular, document-level Role-Based Access Controls (RBAC) guarantee employees can only query info they are explicitly authorized to view.',
          ],
        },
        {
          id: 5, source: 'BCG', date: '2024 Strategy Report',
          title: 'Where is the Value in AI?',
          coreArgument: 'Value realization happens when AI tools are embedded into daily workflows of frontline employees rather than isolated in innovation labs.',
          keyInsight: 'Tools must seamlessly interface with existing systems (ERP, CRM, Intranets) to drive actual productivity gains.',
          primaryRisk: 'Low user adoption because the AI tool requires users to abandon their current systems or learn complex new software.',
          dooraResponse: [
            'Native integrations and robust APIs allow deployment directly into CRMs, ERPs, and internal enterprise communication systems.',
            'Single Sign-On (SSO) and contextual-awareness eliminate the need for employees to learn complex new platforms, maximizing organic adoption.',
          ],
        },
      ],
      ctaHeadline: 'Build on Solid Ground.',
      ctaSubheadline: 'The consensus is clear: without unified data architecture, robust deployment controls, and domain specialization, GenAI is merely a toy. Doora moves it to production.',
      ctaButton: 'Explore Doora Platform',
    },
  },
  id: {
    // Header
    nav: {
      whyDoora: 'Mengapa Doora',
      product: 'Produk',
      research: 'Riset',
      benchmark: 'Perbandingan',
      results: 'Hasil',
      roi: 'ROI',
      getDemo: 'Minta Demo',
    },
    // Hero
    hero: {
      eyebrow: 'Lapisan Pengetahuan AI',
      headline: 'Fondasi yang membuat',
      headlineHighlight: 'investasi AI',
      headlineEnd: 'terbayarkan',
      subheadline: 'Kami berada di antara sistem perusahaan anda dan setiap aplikasi AI asisten operator, pencarian, dan analitik untuk memberikan jawaban yang tepercaya dan sepenuhnya dapat dilacak.',
      ctaPrimary: 'Lihat Aksi',
      ctaSecondary: 'Lihat Hasil Industri',
      chips: ['Sesuai SOC 2', 'Deploy dalam Minggu', 'Tanpa Migrasi'],
      metrics: [
        { value: '73%', label: 'Waktu Jawab Lebih Cepat' },
        { value: '4.2x', label: 'ROI Tahun Pertama' },
        { value: '89%', label: 'Kepercayaan Karyawan' },
        { value: '60%', label: 'Lebih Sedikit Duplikasi' },
        { value: '$2.4M', label: 'Rata-rata Penghematan' },
        { value: '3 Minggu', label: 'Sampai Deployment' },
      ],
    },
    // Layer Architecture
    layers: {
      eyebrow: 'AI Knowledge Layer',
      headline: 'Arsitektur 5 Lapisan untuk ',
      headlineHighlight: 'Kecerdasan Kolektif',
      subheadline: 'Doora memproses data terstruktur, semi-terstruktur, dan tidak terstruktur menjadi Knowledge Graph yang saling terhubung.',
      layer5: { name: 'L5: Aplikasi AI', description: 'Enterprise Copilot, Jendela Pengetahuan, Dasbor, API' },
      layer4: { name: 'L4: Kecerdasan Buatan', description: 'Pencarian semantik, penelusuran graf, pelacakan' },
      layer3: { name: 'L3: Penyimpanan Pengetahuan', description: 'Knowledge Graph, Vector DB, Objek Blob' },
      layer2: { name: 'L2: Pemrosesan Pengetahuan', description: 'Ekstraksi entitas AI, pengelompokan, embedding' },
      layer1: { name: 'L1: Ingesti Data', description: 'Konektor multi-sumber menormalisasi semua jenis data' },
      flowText: 'Jejaring Kecerdasan Kolektif • AI Tumpukan Penuh Terlacak',
      layerLabel: 'Lapisan',
    },
    // Processing Engine
    processing: {
      eyebrow: 'Mesin Pemroses Pengetahuan',
      headline: 'Menyatukan semua bentuk ',
      headlineHighlight: 'struktur data',
      subheadline: 'Doora memiliki pipa AI khusus untuk tipe data Anda shingga Anda bisa menyatukan pencarian ERP dan PDF dalam query yang sama.',
      sourcesLabel: 'Sumber',
      structured: {
        title: 'Data Terstruktur',
        description: 'Data memiliki skema baris & kolom yang ketat. Sangat cocok dan ringkas untuk analitik query.',
        sources: 'ERP, Database, CRM, Sistem IT',
        process: 'NL2SQL → Rencana Kuiri → Analitik OLAP',
      },
      semiStructured: {
        title: 'Semi-Terstruktur',
        description: 'Data memiliki struktur internal yang disepakati (tags) tapi tidak memiliki skema kaku di DB.',
        sources: 'Logs, Tiket Jira, Tiket Zendesk',
        process: 'Parsing JSON → Ekstrak Atribut',
      },
      unstructured: {
        title: 'Data Tidak Terstruktur',
        description: 'Teks bebas, berkas cloud, hasil web yang berisi kekayaan intelektual sesungguhnya perusahaan.',
        sources: 'Dokumen Cloud, Pesan Chat, Riset Web',
        process: 'Pemecahan Teks → Penyimpanan Vektor Semantik',
      }
    },
    // Comparison
    comparison: {
      eyebrow: 'Kesenjangan AI',
      headline: 'proyek AI enterprise',
      headlineHighlight: 'gagal memberikan nilai',
      subheadline: 'Masalahnya bukan model AI Anda. Ini adalah pengetahuan yang terfragmentasi di bawahnya.',
      pain: 'Masalah',
      withDoora: 'Dengan Doora',
      rows: [
        { dimension: 'Waktu Mencari Informasi', pain: '45+ menit mencari di seluruh sistem', solution: 'Jawaban instan dengan atribusi sumber lengkap', impact: '73% lebih cepat' },
        { dimension: 'Kepercayaan pada Respons AI', pain: 'Tidak tahu dari mana jawaban berasal', solution: 'Setiap jawaban dapat dilacak ke dokumen sumber', impact: '89% adopsi' },
        { dimension: 'Berbagi Pengetahuan', pain: 'Tim tersilo, pekerjaan terduplikasi', solution: 'Satu otak perusahaan terpadu untuk semua', impact: '60% lebih sedikit ulang' },
        { dimension: 'Biaya Infrastruktur AI', pain: 'Membangun ulang untuk setiap aplikasi AI baru', solution: 'Satu fondasi mendukung kasus penggunaan AI tak terbatas', impact: '16x lebih murah' },
        { dimension: 'Kesegaran Data', pain: 'Data basi, siklus pembaruan manual', solution: 'Sinkronisasi real-time, informasi selalu terkini', impact: 'Selalu akurat' },
        { dimension: 'Kepatuhan & Audit', pain: 'Tidak bisa membuktikan sumber keputusan AI', solution: 'Jejak audit lengkap untuk setiap respons', impact: '100% dapat diaudit' },
      ],
      quote: "Kami berhenti bertanya 'Bisakah AI kami melakukan ini?' dan mulai bertanya 'Apa lagi yang bisa kami bangun?'",
      quoter: '  VP Transformasi Digital',
    },
    // Features
    features: {
      eyebrow: 'Mengapa Enterprise Terkemuka Memilih Doora',
      headline: 'AI Anda hanya sepintar',
      headlineHighlight: 'lapisan pengetahuan Anda',
      subheadline: 'Berhenti membangun AI di fondasi yang terfragmentasi. Berikan setiap alat, setiap tim, dan setiap alur kerja kecerdasan terpercaya yang sama.',
      items: [
        { title: 'Terhubung dalam Hari, Bukan Bulan', subtitle: 'Integrasi Instan', description: 'Bekerja dengan sistem yang ada tanpa migrasi, tanpa gangguan. Tim Anda terus bekerja sementara AI semakin pintar.', stats: [{ value: '20+', label: 'Konektor' }, { value: '3 minggu', label: 'Deployment' }] },
        { title: 'Satu Otak untuk Seluruh Perusahaan', subtitle: 'Kecerdasan Terpadu', description: 'Marketing tahu apa yang Sales tahu. Engineering menemukan apa yang Legal sudah selesaikan. Semua mendapat manfaat dari pengetahuan kolektif.', stats: [{ value: '60%', label: 'Lebih Sedikit Ulang' }, { value: '4.2x', label: 'Onboarding Lebih Cepat' }] },
        { title: 'Selalu Segar, Selalu Benar', subtitle: 'Sinkronisasi Real-Time', description: 'Dokumen baru? Dapat dicari dalam hitungan detik. Perubahan kebijakan? Semua orang punya yang terbaru. Tidak ada data basi.', stats: [{ value: '~25d', label: 'Kecepatan Update' }, { value: '100%', label: 'Akurasi' }] },
        { title: 'Percaya Setiap Jawaban', subtitle: 'Atribusi Sumber', description: 'Setiap kalimat terhubung ke sumbernya. Arahkan kursor ke respons mana pun untuk melihat dari mana informasi berasal dokumen, bagian, dan tingkat keyakinan. Sempurna untuk audit dan kepatuhan.', stats: [{ value: '45.0', label: 'Sinyal Atribusi' }, { value: '100%', label: 'Dapat Diaudit' }] },
        { title: 'Ekspor Pengetahuan untuk Pelatihan AI', subtitle: 'Hub Integrasi AI', description: 'Kirim pengetahuan terkurasi ke aplikasi AI eksternal untuk pelatihan atau konteks. Pilih dataset generatif untuk LLM/embedding, atau ekspor ML terstruktur untuk klasifikasi, regresi, dan clustering.', stats: [{ value: '4', label: 'Mode Ekspor' }, { value: 'API', label: 'Akses' }] },
        { title: 'Dibangun untuk Enterprise', subtitle: 'Keamanan & Kontrol', description: 'Data Anda tetap milik Anda. Akses berbasis peran, log audit, opsi on-premise. IT dan Legal akan menyukainya.', stats: [{ value: 'SOC 2', label: 'Sesuai' }, { value: 'RBAC', label: 'Kontrol' }] },
      ],
    },
    // Use Cases
    useCases: {
      eyebrow: 'Hasil Terbukti',
      headline: 'Hasil nyata di',
      headlineHighlight: 'setiap industri',
      subheadline: 'Ini bukan proyeksi. Ini adalah hasil terukur dari deployment produksi.',
      before: 'Sebelum',
      after: 'Sesudah',
      annualCost: 'Biaya Tahunan',
      viewDetails: 'Lihat metrik detail',
      showLess: 'Tampilkan lebih sedikit',
      industries: [
        { industry: 'Layanan Keuangan', company: 'Bank Investasi Global', challenge: 'Tim kepatuhan menghabiskan 6+ jam sehari mencari regulasi di 50+ sistem', before: { cost: '$4.2M/tahun' }, after: { cost: '$890K/tahun' }, impact: { primary: '79%', label: 'Pengurangan Biaya' }, quote: '"Tim kepatuhan kami sekarang menangani 3x beban kerja dengan kepercayaan lebih tinggi."', quoter: 'Chief Compliance Officer' },
        { industry: 'Telekomunikasi', company: 'Penyedia Layanan Nasional', challenge: 'Agen dukungan tidak bisa dengan cepat mencari solusi masalah jaringan karena 10+ sistem pengetahuan yang terpisah', before: { cost: '$5.4M/tahun' }, after: { cost: '$1.2M/tahun' }, impact: { primary: '78%', label: 'Pengurangan Biaya' }, quote: '"Agen sekarang menyelesaikan masalah teknis pada panggilan pertama tanpa eskalasi ke Tier 2."', quoter: 'VP Kepuasan Pelanggan' },
        { industry: 'Manufaktur', company: 'Pemasok Otomotif', challenge: 'Engineer membuat ulang solusi yang sudah ada di tempat lain, menyebabkan keterlambatan', before: { cost: '$6.1M/tahun' }, after: { cost: '$1.4M/tahun' }, impact: { primary: '77%', label: 'Lebih Sedikit Ulang' }, quote: '"Kami berhenti menemukan kembali roda. Secara harfiah kami mendesain roda."', quoter: 'Direktur Engineering' },
        { industry: 'Hukum', company: 'Firma Hukum Top 50', challenge: 'Associate menghabiskan 60% waktu untuk riset yang sudah dilakukan partner', before: { cost: '$8.2M/tahun' }, after: { cost: '$2.1M/tahun' }, impact: { primary: '74%', label: 'Peningkatan Efisiensi' }, quote: '"Associate junior sekarang perform seperti mid-level. Itu transformatif."', quoter: 'Managing Partner' },
      ],
      bottomStats: [
        { value: '50+', label: 'Deployment Enterprise' },
        { value: '$18M+', label: 'Penghematan Tahunan' },
        { value: '99.9%', label: 'SLA Uptime' },
      ],
    },
    // ROI
    roi: {
      eyebrow: 'ROI Terukur dari Deployment Produksi',
      headline: 'ROI yang CFO Anda akan',
      headlineHighlight: 'benar-benar percaya',
      subheadline: 'Bukan proyeksi. Bukti dari enterprise seperti Anda.',
      outcomes: [
        { stat: '73%', title: 'Keputusan Lebih Cepat', description: 'Jawaban instan vs. jam mencari' },
        { stat: '60%', title: 'Lebih Sedikit Ulang', description: 'Temukan apa yang sudah diselesaikan orang lain' },
        { stat: '89%', title: 'Adopsi AI', description: 'Ketika orang percaya jawabannya' },
        { stat: '100%', title: 'Siap Audit', description: 'Setiap jawaban dapat dilacak ke sumber' },
        { stat: '4.2x', title: 'Onboarding Lebih Cepat', description: 'Karyawan baru produktif dari hari pertama' },
        { stat: '16x', title: 'Biaya AI Lebih Rendah', description: 'Satu fondasi, aplikasi tak terbatas' },
      ],
      timelineTitle: 'Investasi yang',
      timelineHighlight: 'bertambah seiring waktu',
      timeline: [
        { month: 'Bulan 1', title: 'Hubungkan & Go Live', description: 'Hubungkan sistem inti, lihat peningkatan pencarian langsung' },
        { month: 'Bulan 3', title: 'Nilai Lintas Tim', description: 'Berbagi pengetahuan antar departemen, penghematan waktu terukur' },
        { month: 'Bulan 6+', title: 'Akselerasi AI', description: 'Luncurkan aplikasi AI baru dalam hari, bukan bulan. Pengembalian berlipat.' },
      ],
    },
    // CTA
    cta: {
      badge: 'Lihat Hasil dalam 3 Minggu',
      headline: 'Siap mengubah data perusahaan menjadi',
      headlineHighlight: 'keunggulan kompetitif?',
      subheadline: 'Transformasikan kemampuan AI perusahaan Anda. Kami akan menunjukkan kepada Anda apa yang mungkin dilakukan dengan data aktual Anda.',
      emailPlaceholder: 'Email kerja',
      companyPlaceholder: 'Nama perusahaan',
      submit: 'Jadwalkan Demo',
      trustIndicators: ['Demo personal 30 menit', 'Respons dalam 24 jam'],
      successTitle: 'Anda masuk daftar!',
      successMessage: 'Kami akan menghubungi dalam 24 jam untuk menjadwalkan demo personal Anda. Bersiaplah untuk melihat apa yang bisa dilakukan data Anda.',
      errorMessage: 'Terjadi kesalahan. Silakan coba lagi.',
      quote: '"Percakapan ROI berlangsung 5 menit. Hasilnya berbicara sendiri."',
      quoter: '  CTO, Perusahaan Software Enterprise',
    },
    // Footer
    footer: {
      description: 'AI Knowledge Layer yang mengubah pengetahuan enterprise yang terfragmentasi menjadi kecerdasan terhubung.',
      product: 'Produk',
      company: 'Perusahaan',
      resources: 'Sumber Daya',
      legal: 'Legal',
      copyright: '© 2026 Doora. Hak cipta dilindungi.',
      tagline: '',
      termsOfService: 'Ketentuan Layanan',
      privacyPolicy: 'Kebijakan Privasi',
      security: 'Keamanan',
      about: 'Tentang Kami',
      blog: 'Blog',
      careers: 'Karir',
      contact: 'Kontak',
    },
    // Indonesian Focus Section
    indonesianFocus: {
      eyebrow: 'Integrasi Mendalam Konteks Lokal',
      headline: 'Presisi Tak Tertandingi dalam',
      headlineHighlight: 'Bahasa',
      subheadline: 'Model RAG global generik kesulitan menghadapi idiom lokal dan bahasa campuran (slang Bahasa Indonesia).',
      card1: {
        title: 'Lokalisasi yang Peka Konteks',
        description: 'Berbeda dengan LLM generik yang hanya menerjemahkan konteks secara longgar, Doora memahami jargon enterprise spesifik yang tertanam dalam komunikasi korporat harian berbahasa Indonesia, memastikan nol halusinasi dalam pemetaan data sensitif.',
      },
      card2: {
        title: 'Model Embedding Hibrida',
        description: 'Doora memanfaatkan pipeline embedding yang telah di-fine-tune khusus untuk struktur linguistik Bahasa Indonesia, jauh mengungguli mesin pencarian generik dalam menemukan dokumen yang tepat.',
      },
      research: {
        badge: 'Didukung Riset IEEE',
        published: 'Terbit',
        paperTitle: '"IndoEduBERT: Tailored Multi-Lingual and Multi-Grained Sentence Embeddings for the Indonesian Education Domain"',
        reviewLink: 'Lihat Publikasi',
      },
    },
    // Knowledge Graph diagram
    knowledgeGraph: {
      nodes: {
        analyticsPanel: 'Panel Analitik',
        verifiedAnswer: 'Jawaban Terverifikasi',
        autonomousAgent: 'Agen Otonom',
        factChecker: 'Pemeriksa Fakta',
        dooraKnowledgeGraph: 'Knowledge Graph Doora',
        vectorIndex: 'Indeks Vektor',
        entityResolution: 'Resolusi Entitas',
        githubPR: 'GitHub PR #402',
        confluenceDocs: 'Dokumen Confluence',
        slackThread: 'Thread Slack',
      },
      header: { title: 'Mesin Pelacakan', subtitle: 'Atribusi Sumber Lengkap' },
      matchBadge: '99% Cocok',
    },
    // Neural Network diagram
    neuralNetwork: {
      inputs: { database: 'Database', apis: 'API', crm: 'CRM', documents: 'Dokumen', cloud: 'Cloud', erp: 'ERP' },
      outputs: { copilot: 'Copilot', agent: 'Agen', search: 'Pencarian', analytics: 'Analitik', apps: 'Aplikasi', api: 'API' },
      dataSourcesLabel: 'SUMBER DATA',
      aiOutputsLabel: 'OUTPUT AI',
    },
    // Complex Data Section
    complexData: {
      eyebrow: 'Dibangun untuk Data Dunia Nyata',
      headline: 'Berkembang di Tengah',
      headlineHighlight: 'Kekacauan',
      headlineEnd: 'Data Tidak Terstruktur',
      subheadline: 'Data enterprise tidak pernah rapi; datanya tersebar di berkas hasil pindaian dan arsip lama. Doora meninggalkan pipeline RAG konvensional dan menerapkan metode ekstraksi neural khusus yang dirancang untuk mengatasi kekacauan data-jauh mengungguli platform pencarian standar.',
      features: [
        { title: 'Mengekstrak yang Tak Terekstrak', description: 'Doora menggunakan Vision-Language Models (VLM) dan OCR berbasis LayoutLMv3 untuk mengurai hasil pindaian fisik yang berantakan, tabel kompleks, dan PDF tidak terstruktur.' },
        { title: 'Ekstraksi Relasional Berbasis Graf', description: 'Alih-alih chunking naif yang merusak konteks, kami memetakan entitas lintas berkas lama. Mengembangkan riset GraphRAG SOTA, kami menghubungkan data enterprise yang terisolasi secara dinamis menjadi satu graf yang kohesif.' },
        { title: 'Penalaran Multi-Hop Kontekstual', description: 'Pencarian vektor standar gagal pada dokumen yang dikelola dengan buruk. Kami menggunakan parsing hierarkis untuk menjaga maksud asli, jauh mengungguli RAG generik di lingkungan data tidak terstruktur.' },
      ],
    },
    // Benchmark Section
    benchmark: {
      eyebrow: 'Evaluasi Independen, Hasil Transparan',
      headline: 'Dibandingkan dengan',
      headlineHighlight: 'alternatif lain',
      subheadline: 'Doora vs Lainnya — model sama, data sama, pendekatan retrieval berbeda',
      dooraLabel: 'Doora',
      vsLabel: 'vs',
      bestAlternative: 'Alternatif Terbaik',
      metricLabels: {
        retrievalRelevance: { metric: 'Relevansi Pencarian', insight: 'Kualitas jawaban lebih baik di semua skenario yang diuji' },
        attributionCoverage: { metric: 'Cakupan Atribusi', insight: 'Jawaban lebih mudah dilacak dan diverifikasi' },
        costPerQuery: { metric: 'Biaya per Kueri', insight: 'Trade-off biaya yang transparan untuk akurasi lebih tinggi' },
        incrementalUpdate: { metric: 'Pembaruan Inkremental', insight: 'Kesegaran data operasional lebih baik' },
        graphConstructionCost: { metric: 'Biaya Pembuatan Graf', insight: 'Pembuatan graf 16,4x lebih murah' },
      },
      useCaseSectionTitle: 'Performa per Skenario Penggunaan',
      tableHeaders: { useCase: 'Skenario', relevance: 'Relevansi', coverage: 'Cakupan', takeaway: 'Kesimpulan' },
      useCases: [
        { key: 'factualLookup', useCase: 'Pencarian Faktual', dooraScore: '47%', dooraAttr: '42.6', takeaway: 'Lebih tepercaya untuk penggunaan operasional' },
        { key: 'multiSourceSynthesis', useCase: 'Sintesis Multi-Sumber', dooraScore: '64%', dooraAttr: '62.4', takeaway: 'Sintesis kuat dengan sumber yang jelas' },
        { key: 'thematicDiscovery', useCase: 'Penemuan Tema', dooraScore: '68%', dooraAttr: '66.2', takeaway: 'Transparansi lebih baik untuk pertanyaan organisasi' },
        { key: 'relationshipDiscovery', useCase: 'Penemuan Relasi', dooraScore: '80%', dooraAttr: '74', takeaway: 'Di sinilah Doora paling unggul' },
      ],
      detailedToggle: 'Laporan Performa Detail (vs Alternatif Lain)',
      detailedHeaders: { useCase: 'Skenario', fuseragDoora: 'FuseRAG (Doora)', graphrag: 'GraphRAG', hipporag: 'HippoRAG', adaptiverag: 'AdaptiveRAG', selfrag: 'SelfRAG', convrag: 'Conv. RAG' },
      detailedReport: [
        { useCase: 'Pencarian Faktual', fuserag: '47.2%', graphrag: '42.5%', hipporag: '38.1%', adaptiverag: '39.4%', selfrag: '35.2%', convrag: '31.5%' },
        { useCase: 'Sintesis Multi-Sumber', fuserag: '64.3%', graphrag: '62.4%', hipporag: '51.8%', adaptiverag: '55.1%', selfrag: '48.9%', convrag: '41.2%' },
        { useCase: 'Penemuan Tema', fuserag: '68.5%', graphrag: '66.2%', hipporag: '47.3%', adaptiverag: '46.0%', selfrag: '42.1%', convrag: '38.7%' },
        { useCase: 'Penemuan Relasi', fuserag: '80.1%', graphrag: '74.5%', hipporag: '65.2%', adaptiverag: '61.8%', selfrag: '58.3%', convrag: '45.4%' },
      ],
      research: {
        title: 'Arsitektur Berbasis Riset',
        quote: '"Pendekatan FuseRAG kami secara native menggabungkan sinyal multi-sumber yang jarang (sparse) dengan pola graf topologis yang padat (dense), secara efektif menetralkan dilusi konteks dan mengungguli metode pencarian tradisional dengan menjembatani celah entitas antar dokumen."',
        readThePaper: 'Baca Makalahnya',
        paperTitle: 'FuseRAG: Parallel Multi-Source Knowledge Fusion for Hybrid Graph-Augmented Retrieval-Augmented Generation',
      },
      modal: {
        sampleTest: 'Contoh Uji',
        subtitle: 'Evaluasi Skenario Dunia Nyata',
        userQuery: 'Pertanyaan Pengguna',
        expectedAnswer: 'Jawaban Standar yang Diharapkan',
        dooraOutput: 'Output Doora',
        highRelevanceClarity: 'Relevansi & Kejelasan Tinggi',
        averageAlternative: 'Alternatif Rata-rata',
        lacksNuance: 'Kurang Nuansa / Konteks',
      },
      useCaseSamples: {
        factualLookup: {
          question: 'Berapa pendapatan Q3 untuk wilayah APAC?',
          expected: 'Pendapatan Q3 untuk wilayah APAC adalah $4,2 juta.',
          doora: 'Berdasarkan Laporan Keuangan Regional Q3 (halaman 12), wilayah APAC menghasilkan pendapatan sebesar $4,2 juta.',
          others: 'Pendapatan APAC adalah $4,2 juta. (Sering tidak memiliki konteks terstruktur atau sitasi halaman yang tepat)',
        },
        multiSourceSynthesis: {
          question: 'Bagaimana aturan kepatuhan baru memengaruhi proses onboarding kontraktor remote?',
          expected: 'Kontraktor remote memerlukan modul keamanan tambahan dan pemeriksaan latar belakang yang memakan waktu 2 hari tambahan.',
          doora: 'Menyintesis Kebijakan Kontraktor dan Pembaruan Kepatuhan 2024: Kontraktor remote harus menyelesaikan modul keamanan yang diperluas dan menjalani pemeriksaan latar belakang level-2, menambah sekitar 2 hari pada linimasa onboarding standar mereka.',
          others: 'Kontraktor perlu menjalani pelatihan keamanan. (Sering melewatkan sintesis dari beberapa pembaruan kebijakan dan dampak pastinya)',
        },
        thematicDiscovery: {
          question: 'Apa saja tema utama yang berulang dalam umpan balik negatif pelanggan belakangan ini?',
          expected: 'Sering disebutkan: aplikasi mobile yang crash, dashboard yang lambat dimuat, dan tata letak invoice yang membingungkan.',
          doora: 'Berdasarkan 300+ tiket dukungan terbaru, tema negatif utama adalah: 1. Latensi dashboard saat jam sibuk. 2. Kebingungan seputar item invoice prorata. 3. Crash tak terduga pada aplikasi mobile iOS.',
          others: 'Pelanggan mengeluhkan dashboard dan beberapa masalah tagihan. (Gagal menangkap tema spesifik dan nuansa)',
        },
        relationshipDiscovery: {
          question: 'Siapa stakeholder utama yang menghambat proyek Migrasi SSO, dan inisiatif lain apa yang terkait dengannya?',
          expected: 'Jane Doe adalah approver yang dibutuhkan untuk Migrasi SSO dan juga terkait dengan proyek CRM Overhaul.',
          doora: "Jane Doe (VP of IT) adalah approver yang belum menyetujui sehingga status Migrasi SSO belum bisa berubah menjadi 'Aktif'. Analisis graf menunjukkan ia juga menjadi sponsor proyek untuk inisiatif 'CRM Overhaul' yang akan datang.",
          others: 'Jane Doe adalah kontak untuk Migrasi SSO. (Kesulitan menelusuri hubungan antar entitas untuk menemukan status hambatan atau inisiatif terkait lainnya)',
        },
      },
    },
    // Product Page
    productPage: {
      badge: 'Platform Inti Doora',
      headline: 'Fondasi AI untuk',
      headlineHighlight: 'Enterprise',
      subheadline: 'Ekosistem Retrieval-Augmented Generation yang terintegrasi penuh, dibangun untuk akurasi, kepatuhan, dan operasional tanpa cela di Asia Tenggara.',
      features: [
        { title: 'NLP Bahasa Indonesia yang Disesuaikan', desc: 'Model embedding khusus untuk slang Bahasa Indonesia & kosakata bisnis formal.' },
        { title: 'Siap On-Premise', desc: 'Kontrol penuh. Deploy sepenuhnya di dalam firewall Anda sendiri tanpa kebocoran data.' },
        { title: 'Knowledge Graph Otomatis', desc: 'Ubah dokumen terstruktur dan tidak terstruktur menjadi pengetahuan relasional.' },
        { title: 'Akses Berbasis Peran untuk Enterprise', desc: 'ACL granular hingga level potongan dokumen.' },
      ],
      capabilitiesHeadline: 'Platform',
      capabilitiesHighlight: 'Kapabilitas',
      capabilitiesSubheadline: 'Jelajahi berbagai alat dan fitur yang dirancang untuk membuat data enterprise mudah diakses dan aman tanpa pernah mengorbankan privasi Anda.',
      capabilities: [
        { title: 'Pencarian Semantik Cerdas', description: "Rasakan AI percakapan yang benar-benar memahami konteks. Ajukan pertanyaan ke seluruh 'otak' organisasi Anda-dari codebase hingga catatan rapat-dan dapatkan jawaban tersintesis dengan konteks mendalam secara instan." },
        { title: 'Atribusi Transparan & Terlacak', description: 'Percayai output AI Anda dengan verifikasi 100%. Setiap jawaban yang dihasilkan menyertakan sitasi mendalam dan dapat diklik kembali ke dokumen sumber yang tepat, menjaga jejak audit dan kepatuhan penuh.' },
        { title: 'Knowledge Graph Interaktif', description: 'Visualisasikan hubungan tersembunyi di seluruh data enterprise Anda. Mesin kami secara otomatis mengekstrak koneksi semantik antar dokumen, repositori, orang, dan topik, membentuk gambaran menyeluruh tentang organisasi Anda.' },
        { title: 'Hub Pengetahuan Universal', description: 'Konsolidasikan kecerdasan Anda yang tersebar ke dalam satu tampilan terpadu. Pantau status ingesti, kelola silo data, dan atur sumber aktif yang menyuplai model AI kustom Anda.' },
        { title: 'Integrasi Plug-and-Play', description: 'Terhubung dengan aman ke lebih dari 35+ platform enterprise langsung dari awal. Dari GitHub dan GitLab hingga Google Scholar dan Slack, sinkronkan data Anda dengan aman tanpa menulis satu baris kode ETL pun.' },
      ],
      ctaHeadline: 'Siap melihat Doora beraksi?',
      ctaSubheadline: 'Jadwalkan demo personal dan lihat bagaimana Doora mengubah pengetahuan enterprise Anda menjadi keunggulan kompetitif.',
      ctaButton: 'Minta Demo',
    },
    // Research Page
    researchPage: {
      headline: 'Mengapa AI Enterprise',
      headlineHighlight: 'Gagal',
      subheadline: 'Transisi dari program pilot ke operasional produksi adalah titik di mana sebagian besar inisiatif AI enterprise gagal total.',
      labels: { coreArgument: 'Argumen Inti', keyInsight: 'Wawasan Utama', primaryRisk: 'Risiko Utama', dooraDelivers: 'Bagaimana Doora Mengatasi Ini' },
      reports: [
        {
          id: 1, source: 'Gartner', date: 'Januari 2024',
          title: '30% Proyek Generative AI Akan Ditinggalkan Setelah Proof of Concept pada Akhir 2025',
          coreArgument: 'Saat organisasi beralih dari pilot ke produksi, kualitas data yang buruk, kontrol risiko yang tidak memadai, dan nilai bisnis yang tidak jelas menyebabkan banyak proyek ditinggalkan.',
          keyInsight: 'Proyek pilot berhasil di lingkungan terbatas namun gagal diskalakan karena masalah data tidak terstruktur dan pembengkakan biaya.',
          primaryRisk: 'Biaya yang terbuang untuk eksperimen LLM yang tidak menghasilkan ROI operasional sama sekali.',
          dooraResponse: [
            'Penataan data sebelum retrieval melalui Knowledge Graph otomatis menjamin fidelitas data yang tinggi.',
            'Kontrol biaya yang transparan memprediksi dan membatasi penggunaan LLM bahkan sebelum satu token pun dihasilkan.',
          ],
        },
        {
          id: 2, source: 'McKinsey', date: 'Akhir 2023 / Awal 2024',
          title: 'State of AI: Keharusan untuk Penskalaan Enterprise',
          coreArgument: 'Mencapai dampak nyata dari AI membutuhkan perancangan ulang model operasional secara fundamental, bukan sekadar mengadopsi teknologinya.',
          keyInsight: 'Perusahaan yang mendapatkan nilai terbesar dari AI sangat fokus pada pelatihan spesifik domain dan integrasi operasional.',
          primaryRisk: 'Menggunakan model generik untuk alur kerja enterprise yang spesifik menyebabkan penanganan kosakata milik perusahaan menjadi tidak akurat.',
          dooraResponse: [
            'Model embedding yang disesuaikan dengan domain di-fine-tune khusus untuk kekayaan bahasa enterprise Indonesia, termasuk jargon korporat dan dialek lokal.',
            'Alat integrasi operasional yang mendalam memungkinkan alur kerja AI dipetakan langsung ke proses bisnis tim Anda yang sudah berjalan.',
          ],
        },
        {
          id: 3, source: 'MIT Sloan', date: 'Maret 2024',
          title: 'Mengapa Begitu Banyak Proyek Generative AI Gagal',
          coreArgument: "Kegagalan berakar dari 'utang infrastruktur' di mana perusahaan mencoba menumpuk AI di atas silo data yang terfragmentasi dan tidak terkelola.",
          keyInsight: 'Model AI hanya sebaik arsitektur data yang mendasarinya. Output RAG yang tidak terverifikasi dengan cepat mengikis kepercayaan pengguna.',
          primaryRisk: 'Halusinasi yang menyebabkan kesalahan bisnis kritis atau pelanggaran regulasi.',
          dooraResponse: [
            'Pemisahan ketat antara retrieval dokumen yang berbasis fakta dan generasi teks sepenuhnya menghilangkan halusinasi LLM yang tidak terverifikasi.',
            'Setiap klaim faktual diverifikasi silang dengan dokumen sumber aslinya, memberikan pengguna sitasi yang tepat dan dapat diklik.',
          ],
        },
        {
          id: 4, source: 'IBM Institute for Business Value', date: 'Studi CEO 2024',
          title: 'Dilema AI Enterprise: Tata Kelola dan Etika',
          coreArgument: 'Para CEO menyadari potensi GenAI, namun keamanan data, privasi, dan kepatuhan regulasi tetap menjadi hambatan utama dalam integrasinya.',
          keyInsight: 'Enterprise membutuhkan lingkungan on-premise atau yang sangat aman untuk memastikan pengetahuan milik perusahaan tidak bocor ke model publik.',
          primaryRisk: 'Terekspos-nya kekayaan intelektual sensitif atau data pelanggan ke LLM pihak ketiga.',
          dooraResponse: [
            'Deployment on-premise dan virtual private cloud (VPC) yang sepenuhnya terisolasi memastikan IP rahasia tidak pernah keluar dari firewall Anda.',
            'Role-Based Access Control (RBAC) granular hingga level dokumen menjamin karyawan hanya bisa mengakses informasi yang secara eksplisit mereka miliki izinnya.',
          ],
        },
        {
          id: 5, source: 'BCG', date: 'Laporan Strategi 2024',
          title: 'Di Mana Nilai dari AI?',
          coreArgument: 'Realisasi nilai terjadi ketika alat AI tertanam dalam alur kerja harian karyawan garda depan, bukan terisolasi di lab inovasi.',
          keyInsight: 'Alat harus terhubung mulus dengan sistem yang sudah ada (ERP, CRM, Intranet) untuk mendorong peningkatan produktivitas yang nyata.',
          primaryRisk: 'Adopsi pengguna yang rendah karena alat AI mengharuskan pengguna meninggalkan sistem mereka saat ini atau mempelajari perangkat lunak baru yang rumit.',
          dooraResponse: [
            'Integrasi native dan API yang andal memungkinkan deployment langsung ke dalam CRM, ERP, dan sistem komunikasi internal enterprise.',
            'Single Sign-On (SSO) dan kesadaran kontekstual menghilangkan kebutuhan karyawan untuk mempelajari platform baru yang rumit, memaksimalkan adopsi organik.',
          ],
        },
      ],
      ctaHeadline: 'Bangun di Atas Fondasi yang Kokoh.',
      ctaSubheadline: 'Konsensusnya jelas: tanpa arsitektur data yang terpadu, kontrol deployment yang andal, dan spesialisasi domain, GenAI hanyalah sekadar mainan. Doora membawanya ke tahap produksi.',
      ctaButton: 'Jelajahi Platform Doora',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('id');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'id' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;
